import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { AGREEMENT_TYPE, DOWNLOAD_TYPE } from './constants'

const useDownloadSlider = ({
  actions,
  agreements = [],
  entity,
  getHints = () => [],
  innerProps = {},
  onSubmit,
  user,
}) => {
  const priceRef = useRef(0)
  // Setup
  const { company } = user.currentUser.payload
  const isCurrentUserSetSftp = useMemo(
    () => company && company.sftp_server && company.sftp_username,
    [company]
  )

  const getInitialFormParams = agr => ({
    agreement_id: agr?.id,
    branding_type: agr?.can_download_content?.allowed_branding_types[0],
    format: innerProps.defaultFormat,
    download_to_sftp: false,
  })

  const sortAgreements = () => {
    const res = {
      active: [],
      inactive: [],
    }

    agreements.forEach(agr => {
      const {
        can_download_content: { can, why_not_reason },
      } = agr

      const updAgr = {
        ...agr,
        disabled: can,
      }

      if (can || (why_not_reason !== 'none' && !entity.free_access)) {
        res.active.push(updAgr)
      } else {
        res.inactive.push(updAgr)
      }
    })

    return {
      ...res,
      modifyAgrList: [...res.active, ...res.inactive],
    }
  }

  // State
  const [currentAgreement, setCurrentAgreement] = useState(() => {
    const { active, modifyAgrList } = sortAgreements(agreements)
    return active[0] || modifyAgrList[0]
  })

  const [form, setFormState] = useState(() =>
    getInitialFormParams(currentAgreement)
  )
  // console.log(form);
  const [isScreener, setScreenerState] = useState(false)
  const [sftpIsOpened, setSftpOpenState] = useState(false)
  // Data
  const hints = useMemo(
    () => getHints(form, currentAgreement),
    [currentAgreement, form, getHints, entity]
  )

  const price = useMemo(() => {
    const val = entity[`${form.branding_type}_price`]
    if (val) {
      priceRef.current = val
      return val
    }
    return priceRef.current
  }, [entity, form])

  const isRepeated = useMemo(() => {
    const can_download = currentAgreement?.can_download_content || {}
    const { one_off_already_downloaded_branding_types, download_type } =
      can_download
    const one_off_DBT = one_off_already_downloaded_branding_types || []

    if (download_type === AGREEMENT_TYPE.ONE_OFF && one_off_DBT.length) {
      return one_off_DBT.includes(form.branding_type)
    }

    return download_type === DOWNLOAD_TYPE.REPEATED && !entity.free_access
  }, [currentAgreement, form.branding_type, entity.free_access])

  // Handlers
  const handleFormChange = useCallback(
    (event, newForm) => {
      const hasChange = +currentAgreement.id !== +newForm.agreement_id
      const agr = hasChange
        ? agreements.find(item => +item.id === +newForm.agreement_id)
        : {}

      hasChange && setCurrentAgreement(agr)
      setFormState(hasChange ? getInitialFormParams(agr) : newForm)
      setSftpOpenState(newForm.download_to_sftp && !isCurrentUserSetSftp)
      setScreenerState(newForm.format === 'md;mp4;screener_free')
    },
    [agreements, isCurrentUserSetSftp, currentAgreement]
  )

  const handleFormSubmit = useCallback(() => {
    if (form.download_to_sftp && !isCurrentUserSetSftp) {
      setSftpOpenState(true)
    } else {
      onSubmit(form, currentAgreement)
    }
  }, [currentAgreement, form, isCurrentUserSetSftp, onSubmit])

  const handleSftpClose = useCallback(() => setSftpOpenState(false), [])

  const handleSftpSuccess = useCallback(() => {
    actions.user.getCurrentUser().catch(() => {})
    setSftpOpenState(false)
  }, [actions])

  const handleUpdateFormState = useCallback(
    (newValues = {}) => {
      setFormState({ ...form, ...newValues })
    },
    [form]
  )

  const { active, inactive, modifyAgrList } = useMemo(
    () => sortAgreements(),
    [agreements, entity.world_exclusive]
  )

  // Effects
  useEffect(() => {
    setFormState(getInitialFormParams(currentAgreement))
  }, [innerProps.defaultFormat])

  return {
    currentAgreement,
    form,
    handleFormChange,
    handleFormSubmit,
    handleSftpClose,
    handleSftpSuccess,
    handleUpdateFormState,
    hints,
    isScreener,
    price,
    sftpIsOpened,
    isRepeated,
    activeAgreements: active,
    inactiveAgreements: inactive,
    modifyAgrList,
  }
}

export { useDownloadSlider }
